import { Flex, Text, Tooltip } from '@chakra-ui/react';

import { ColumnDef, createColumnHelper } from '@tanstack/react-table';

import { Badge, BadgeStatus } from '@aslanpay/portal-common-lib';

import { ReconciliationRunStatusValues } from 'app/constants/reconciliations';

import { SalaryReconciliationRunRow } from 'app/types/salaryReconciliations';

import { formatDateTime, formatRunDuration, formatRunScope } from 'app/utils/reconciliations';

/**
 * @description Shows a checked / failed pair, with the failed count in red when it is not zero.
 */
const renderCheckedFailed = (checked: number, failed: number) => (
  <Flex gap="4px">
    <Text>{checked}</Text>
    <Text color="brand.darkGray">/</Text>
    <Text color={failed > 0 ? 'red.500' : undefined}>{failed}</Text>
  </Flex>
);

/**
 * @description Columns of the salary accrual reconciliation runs table; clicking a row opens the run.
 */
function getSalaryReconciliationColumns(onSelect: (run: SalaryReconciliationRunRow) => void) {
  const columnHelper = createColumnHelper<SalaryReconciliationRunRow>();
  const cellContext = {
    getCellContext: ({ row }: { row: { original: SalaryReconciliationRunRow } }) => ({
      onClick: () => onSelect(row.original),
      cursor: 'pointer',
      'data-testid': `salary-reconciliation-row-${row.original.id}`,
    }),
  };

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const columns: ColumnDef<SalaryReconciliationRunRow, any>[] = [
    columnHelper.accessor('status', {
      header: 'Status',
      enableSorting: false,
      cell: (info) => {
        const status = ReconciliationRunStatusValues[info.row.original.status];
        return (
          <Badge
            value={status?.value ?? info.getValue()}
            status={(status?.color ?? 'created') as BadgeStatus}
          />
        );
      },
      meta: cellContext,
    }),
    columnHelper.accessor('companyIds', {
      id: 'scope',
      header: 'Scope',
      enableSorting: false,
      cell: (info) => {
        const run = info.row.original;
        const scope = formatRunScope(run.companyIds, run.companyNames);
        return run.companyNames.length > 2 ? (
          <Tooltip label={run.companyNames.join(', ')} placement="top">
            <Text>{scope}</Text>
          </Tooltip>
        ) : (
          scope
        );
      },
      meta: cellContext,
    }),
    columnHelper.accessor('automated', {
      id: 'triggeredBy',
      header: 'Triggered by',
      enableSorting: false,
      cell: (info) =>
        info.getValue() ? 'Nightly run' : info.row.original.triggeredByName || 'An admin',
      meta: cellContext,
    }),
    columnHelper.accessor('queuedAt', {
      header: 'Queued',
      enableSorting: false,
      cell: (info) => formatDateTime(info.getValue()),
      meta: cellContext,
    }),
    columnHelper.accessor('startedAt', {
      header: 'Started',
      enableSorting: false,
      cell: (info) => formatDateTime(info.getValue()),
      meta: cellContext,
    }),
    columnHelper.accessor('completedAt', {
      header: 'Finished',
      enableSorting: false,
      cell: (info) => formatDateTime(info.getValue()),
      meta: cellContext,
    }),
    columnHelper.display({
      id: 'duration',
      header: 'Duration',
      cell: ({ row }) => formatRunDuration(row.original),
      meta: cellContext,
    }),
    columnHelper.display({
      id: 'companies',
      header: 'Companies checked / failed',
      cell: ({ row }) =>
        renderCheckedFailed(row.original.companiesChecked, row.original.companiesFailed),
      meta: cellContext,
    }),
    columnHelper.display({
      id: 'individuals',
      header: 'Individuals checked / not checked',
      cell: ({ row }) =>
        renderCheckedFailed(row.original.individualsChecked, row.original.individualsNotChecked),
      meta: cellContext,
    }),
    columnHelper.display({
      id: 'problems',
      header: 'Problems / new / resolved',
      cell: ({ row }) =>
        `${row.original.problems} / ${row.original.newProblems} / ${row.original.resolvedProblems}`,
      meta: cellContext,
    }),
  ];

  return columns;
}

export default getSalaryReconciliationColumns;
